/**
 * UserProfile -- Semi-public user profile page with real Firestore data.
 * Route: /user/:userId
 *
 * Redesigned with glass panel hero card, gradient banner with ghosted monogram,
 * MonoAvatar, pill tabs (About / Posts / Circles / Badges), and badge grid.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { UserX, ArrowLeft, User, MapPin, CheckCircle, Clock, Mail, MessageCircle, Heart, Edit2, Award, UserCheck, LogIn, Send, Users } from 'react-feather';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import SEO from '../components/SEO';
import LikeButton from '../components/LikeButton';
import MonoAvatar from '../components/MonoAvatar';
import { formatTimestamp } from '../utils/dateUtils';
import { getMessageUrl } from '../utils/messageUtils';
import { respondToMessageRequest } from '../services/messageService';
import { forgetRequest, markPrompted } from '../hooks/useMessageRequests';
import { trackRequestAnswered } from '../components/messageRequestAnalytics';
import '../styles/messageRequest.css';
import {
  getPublicProfile,
  getUserBadges,
  getUserOnlineStatus,
  logProfileView,
  getUserPosts,
} from '../services/profileService';
import { getChatId } from '../services/messageService';
import { capture } from '../services/posthog';
import { addPersonFromUserEurW } from '../firebaseConfig';
import BadgeDisplay from '../components/BadgeDisplay.tsx';
import type {
  GetPublicProfileResponse,
  GetUserBadgesResponse,
  OnlineStatusData,
  PplTalkMessage,
} from '../types/pplTalk';
import type { RoomComposeTarget } from '../types/room';
import { normalizeInterests } from '../utils/interests';
import { isLocationVerifiedFor } from '../utils/locationVerification';
import '../styles/userProfile.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

function calculateAge(dateOfBirth: string | undefined | null): number | null {
  if (!dateOfBirth) return null;
  try {
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  } catch {
    return null;
  }
}

function lookingForLabel(value: string | undefined): string {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function UserProfile() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // PPL-425 — arrived from the home desk's match card: the room invite keeps
  // the 'match' attribution, and a way back to the desk sits above the hero.
  const cameFrom = (location.state as { from?: string } | null)?.from;
  // PPL-506 — the rooms composer opens candidate profiles too. Same match
  // attribution, but back goes to the rooms flow, so it must not say home.
  const fromHomeMatch = cameFrom === 'home_match' || cameFrom === 'rooms_match';
  // PPL-429 — arrived from a message request's "view profile": the request is
  // still pending, so the answer travels with them instead of being lost.
  const navState = location.state as { from?: string; chatId?: string } | null;
  const pendingRequestChatId = navState?.from === 'message_request' ? navState?.chatId ?? null : null;
  const [answeringRequest, setAnsweringRequest] = useState(false);
  const [requestError, setRequestError] = useState('');

  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();

  // Data state
  const [profile, setProfile] = useState<GetPublicProfileResponse | null>(null);
  const [badges, setBadges] = useState<GetUserBadgesResponse | null>(null);
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatusData | null>(null);
  const [posts, setPosts] = useState<PplTalkMessage[]>([]);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [lastPostId, setLastPostId] = useState<string | undefined>();

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'about' | 'posts' | 'circles' | 'badges'>('about');
  const [loadingMore, setLoadingMore] = useState(false);
  const [addState, setAddState] = useState<'idle' | 'adding' | 'added' | 'error'>('idle');
  const [addError, setAddError] = useState<string | null>(null);

  const answerPendingRequest = useCallback(async (action: 'accept' | 'decline') => {
    if (!pendingRequestChatId || answeringRequest) return;
    setAnsweringRequest(true);
    try {
      const {status, changed} = await respondToMessageRequest(pendingRequestChatId, action);
      // PPL-432, same rule as the prompt and the banner: the server's answer,
      // counted only when this call is the one that changed it.
      if (changed) {
        trackRequestAnswered(
          status === 'accepted' ? 'accept' : status === 'declined' ? 'decline' : action,
          'profile'
        );
      }
      // Same as the conversation banner: an answer here retires the request
      // for the app-wide prompt too, not just for this page.
      forgetRequest(pendingRequestChatId);
      markPrompted(pendingRequestChatId);
      // With the recipient, exactly as the prompt and the requests section
      // do: without it the thread opens headed "User", skips the block check,
      // and sends the first reply with an empty recipientId.
      navigate(action === 'accept' ? `/messages/${pendingRequestChatId}` : '/messages', {
        state: action === 'accept'
          ? { recipientId: userId, recipientName: profile?.tetherHandle || '' }
          : undefined,
      });
    } catch {
      // The same neutral line the prompt and the conversation banner show:
      // a dead-looking button is worse than a sentence.
      setRequestError("couldn't do that — it's still waiting in requests");
      setAnsweringRequest(false);
    }
  }, [pendingRequestChatId, answeringRequest, navigate, userId, profile?.tetherHandle]);

  const handleAddPerson = useCallback(async () => {
    if (!userId || !user?.uid || user.uid === userId) return;
    setAddState('adding');
    setAddError(null);
    try {
      const result = await addPersonFromUserEurW({ linkedUid: userId });
      const data = result.data as { success: boolean; alreadyAdded?: boolean };
      if (data?.success) {
        setAddState('added');
      } else {
        setAddState('error');
        setAddError('Could not add');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add';
      setAddState('error');
      setAddError(msg);
    }
  }, [userId, user?.uid]);

  // ─── invite to room (PPL-210) ─────────────────────────────────────────
  // Hands /rooms a fully-resolved target so the composer skips the contacts
  // picker and lands on the reason/send step (the same prefill path
  // meet-a-match uses). The uid travels in router state, never in the URL.
  //
  // ANONYMOUS half: this page is handle-only, so the handle is the ONLY name
  // that crosses over — there is no firstName to leak (PPL-202).
  const handleInviteToRoom = useCallback(() => {
    if (!userId || !user?.uid || user.uid === userId) return;
    capture('room_cta_tapped', {
      cta: fromHomeMatch ? 'match_profile_invite_to_room' : 'profile_invite_to_room',
      action: 'create',
      mediation_id: null,
      platform: 'web',
    });
    const roomTarget: RoomComposeTarget = {
      uid: userId,
      handle: (profile?.tetherHandle || '').replace(/^@/, ''),
      avatar: profile?.profileImageUrl || null,
      source: fromHomeMatch ? 'match' : 'profile',
    };
    navigate('/rooms', { state: { roomTarget } });
  }, [userId, user?.uid, profile?.tetherHandle, profile?.profileImageUrl, navigate, fromHomeMatch]);

  // ─── Load profile data (no auth required) ─────────────────────────────
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const promises: Promise<any>[] = [
          getPublicProfile(userId!),
          getUserBadges(userId!),
        ];

        if (isAuthenticated) {
          promises.push(getUserOnlineStatus(userId!));
        }

        const results = await Promise.allSettled(promises);

        if (cancelled) return;

        const profileData = results[0];
        const badgesData = results[1];
        const statusData = results[2];

        if (profileData.status === 'fulfilled') {
          setProfile(profileData.value);
        } else {
          setError('User not found');
          setLoading(false);
          return;
        }

        if (badgesData.status === 'fulfilled') {
          setBadges(badgesData.value);
        }

        if (statusData?.status === 'fulfilled') {
          setOnlineStatus(statusData.value);
        }

        if (isAuthenticated && user?.uid && user.uid !== userId) {
          logProfileView(userId!);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [userId, isAuthenticated, user?.uid]);

  // ─── Load user's posts (no auth required) ─────────────────────────────
  useEffect(() => {
    if (!profile?.id) return;

    let cancelled = false;

    async function loadPosts() {
      try {
        const data = await getUserPosts(profile!.id, 10);
        if (!cancelled) {
          setPosts(data.messages || []);
          setHasMorePosts(data.hasMore || false);
          setLastPostId(data.lastMessageId);
        }
      } catch {
        // Non-critical
      }
    }

    loadPosts();
    return () => { cancelled = true; };
  }, [profile?.id]);

  const loadMorePosts = useCallback(async () => {
    if (!profile?.id || loadingMore || !hasMorePosts) return;
    setLoadingMore(true);
    try {
      const data = await getUserPosts(profile.id, 10, lastPostId);
      setPosts((prev) => [...prev, ...(data.messages || [])]);
      setHasMorePosts(data.hasMore || false);
      setLastPostId(data.lastMessageId);
    } catch {
      // Non-critical
    } finally {
      setLoadingMore(false);
    }
  }, [profile?.id, loadingMore, hasMorePosts, lastPostId]);

  // ─── Derived values ────────────────────────────────────────────────────
  const displayName = profile?.tetherHandle || 'User';
  const handle = profile?.tetherHandle ? `@${profile.tetherHandle.replace(/^@/, '')}` : '';
  const age = profile?.showAge !== false ? calculateAge(profile?.dateOfBirth) : null;
  const interests = normalizeInterests(profile?.interests);
  const locationStr = [profile?.city, profile?.country].filter(Boolean).join(', ');
  const isVerified = isLocationVerifiedFor(profile);
  const isOnline = onlineStatus?.isOnline ?? false;
  const hue = hashHue(displayName || 'User');

  const earnedBadges = badges
    ? Object.entries(badges.badges || {})
        .filter(([, b]) => b.earned)
        .map(([badgeId, b]) => ({
          ...b,
          ...(badges.badgeDefinitions?.[badgeId] || {}),
          id: badgeId,
        }))
    : [];

  const allBadgeDefs = badges?.badgeDefinitions
    ? Object.entries(badges.badgeDefinitions).map(([badgeId, def]) => ({
        ...def,
        id: badgeId,
        earned: badges.badges?.[badgeId]?.earned ?? false,
      }))
    : [];

  // getPublicProfile never returns createdAt; lastActive = lastAppOpen || updatedAt || createdAt
  const rawLastActive = profile?.lastActive;
  let lastActiveParsed: Date | null = null;
  if (rawLastActive instanceof Date) lastActiveParsed = rawLastActive;
  else if (typeof rawLastActive === 'string' || typeof rawLastActive === 'number') lastActiveParsed = new Date(rawLastActive);
  else if (rawLastActive?._seconds) lastActiveParsed = new Date(rawLastActive._seconds * 1000);
  else if (rawLastActive?.seconds) lastActiveParsed = new Date(rawLastActive.seconds * 1000);
  const lastActiveMonth = lastActiveParsed && !Number.isNaN(lastActiveParsed.getTime())
    ? lastActiveParsed.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;
  const activityLabel = lastActiveMonth ? `active ${lastActiveMonth}` : 'Recently active';

  // ─── SEO structured data ──────────────────────────────────────────────
  const personSchema = profile ? {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: displayName,
    ...(handle ? { alternateName: handle } : {}),
    ...(profile.aboutMe ? { description: profile.aboutMe } : {}),
    ...(profile.profileImageUrl ? { image: profile.profileImageUrl } : {}),
    ...(profile.city ? { address: { '@type': 'PostalAddress', addressLocality: profile.city } } : {}),
    ...(interests.length > 0 ? { knowsAbout: interests } : {}),
    url: `https://pplkpr.ai/user/${userId}`,
  } : null;

  const seoDescription = profile
    ? [
        profile.aboutMe?.slice(0, 120),
        profile.city ? `Based in ${profile.city}` : null,
        interests.length > 0 ? `Interests: ${interests.slice(0, 3).join(', ')}` : null,
      ].filter(Boolean).join(' - ') || `${displayName} on pplkpr`
    : 'User profile on pplkpr';

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <div className="feed">
        {/* SEO meta tags */}
        {profile && (
          <>
            <SEO
              title={`${displayName} ${handle ? `(${handle})` : ''} | pplkpr`}
              description={seoDescription}
              url={`https://pplkpr.ai/user/${userId}`}
              image={profile.profileImageUrl || undefined}
              type="website"
            />
            {personSchema && (
              <Helmet>
                <script type="application/ld+json">
                  {JSON.stringify(personSchema)}
                </script>
              </Helmet>
            )}
          </>
        )}

        <div className="up-shell">
          {loading && (
            <div className="profile-loading">
              <div className="profile-skeleton">
                <div className="skeleton-avatar" />
                <div className="skeleton-lines">
                  <div className="skeleton-line wide" />
                  <div className="skeleton-line medium" />
                  <div className="skeleton-line narrow" />
                </div>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="up-error">
              <UserX size={32} />
              <h2>profile not found</h2>
              <p>{error}</p>
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                go back
              </button>
            </div>
          )}

          {profile && !loading && (
            <>
              {/* Back button */}
              <button className="up-back" onClick={() => navigate(-1)}>
                <ArrowLeft size={14} />
                <span>{cameFrom === 'home_match' ? 'back to home' : 'back'}</span>
              </button>

              {/* Hero card */}
              <div className="glass-panel up-hero" style={{ padding: 0 }}>
                {/* Gradient banner */}
                <div
                  className="up-banner"
                  style={{
                    background: `linear-gradient(135deg, oklch(0.45 0.22 ${hue}) 0%, oklch(0.6 0.28 ${(hue + 60) % 360}) 100%)`,
                  }}
                >
                  <div className="up-banner__ghost">{displayName[0]}</div>
                </div>

                <div className="up-hero__body">
                  <div className="up-hero__top">
                    {/* Large avatar */}
                    <div className="up-avatar">
                      {profile.profileImageUrl ? (
                        <img src={profile.profileImageUrl} alt={displayName} className="up-avatar__img" />
                      ) : (
                        <div
                          className="up-avatar__mono"
                          style={{
                            background: `linear-gradient(135deg, oklch(0.55 0.22 ${hue}), oklch(0.65 0.25 ${(hue + 60) % 360}))`,
                          }}
                        >
                          {displayName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                        </div>
                      )}
                      {isAuthenticated && isOnline && <span className="up-avatar__online" title="Online now" />}
                    </div>

                    {/* Name / handle / activity */}
                    <div className="up-hero__info">
                      <h1 className="up-name">
                        {displayName}
                        {age && <span className="up-age">, {age}</span>}
                      </h1>
                      <div className="up-meta">
                        {handle && <span>{handle}</span>}
                        {handle && <span>·</span>}
                        <span>{activityLabel}</span>
                      </div>
                    </div>

                    {pendingRequestChatId && (
                      <div className="mr-banner" style={{ margin: '0 0 12px' }}>
                        <span className="mr-banner-text">wants to send you a message</span>
                        <div className="mr-banner-actions">
                          <button
                            className="mr-banner-btn mr-banner-btn--primary"
                            onClick={() => answerPendingRequest('accept')}
                            disabled={answeringRequest}
                          >
                            accept
                          </button>
                          <button
                            className="mr-banner-btn"
                            onClick={() => answerPendingRequest('decline')}
                            disabled={answeringRequest}
                          >
                            decline
                          </button>
                        </div>
                        {requestError && <div className="mr-error">{requestError}</div>}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="up-actions">
                      {isAuthenticated && user?.uid && userId && user.uid !== userId && (
                        <button
                          className="up-btn up-btn--ghost"
                          onClick={() => navigate(`/messages/${getChatId(user.uid, userId)}`, {
                            state: { recipientId: userId, recipientName: profile.tetherHandle || displayName }
                          })}
                        >
                          <Send size={12} />
                          <span>send DM</span>
                        </button>
                      )}
                      {isAuthenticated && user?.uid && userId && user.uid !== userId && (
                        <button
                          className="up-btn up-btn--ghost"
                          onClick={handleInviteToRoom}
                        >
                          <Users size={12} />
                          <span>invite to room</span>
                        </button>
                      )}
                      {!isAuthenticated && (
                        <button className="up-btn up-btn--ghost" onClick={redirectToLogin}>
                          <LogIn size={12} />
                          <span>log in to connect</span>
                        </button>
                      )}
                      {isAuthenticated && user?.uid && userId && user.uid !== userId && (
                        <button
                          className="primary-button"
                          style={{ padding: '8px 14px' }}
                          onClick={handleAddPerson}
                          disabled={addState === 'adding' || addState === 'added'}
                          title={addError || undefined}
                        >
                          {addState === 'adding' && 'adding…'}
                          {addState === 'added' && '✓ added'}
                          {addState === 'idle' && '+ add'}
                          {addState === 'error' && 'try again'}
                        </button>
                      )}
                    </div>

                    {/* PPL-429 — a stranger's first message is a request, and
                        saying so before they type is kinder than after. */}
                    {isAuthenticated && user?.uid && userId && user.uid !== userId && !pendingRequestChatId && (
                      <div className="mr-profile-note">if you two aren't connected yet, this goes as a request</div>
                    )}
                  </div>

                  {/* Bio */}
                  {profile.aboutMe && (
                    <p className="up-bio">{profile.aboutMe}</p>
                  )}

                  {/* Stats row */}
                  <div className="up-stats">
                    <div className="up-stat">
                      <div className="up-stat__value">{posts.length}{hasMorePosts ? '+' : ''}</div>
                      <div className="up-stat__label">posts</div>
                    </div>
                    <div className="up-stat">
                      <div className="up-stat__value">{earnedBadges.length}</div>
                      <div className="up-stat__label">badges</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pill tabs */}
              <div className="up-tabs-wrap">
                <div className="pill-tabs">
                  {[
                    { id: 'about' as const, label: 'about' },
                    { id: 'posts' as const, label: 'posts', count: posts.length },
                    { id: 'badges' as const, label: 'badges', count: earnedBadges.length },
                  ].map((t) => (
                    <button
                      key={t.id}
                      className={`pill-tab ${activeTab === t.id ? 'pill-tab--active' : ''}`}
                      onClick={() => setActiveTab(t.id)}
                    >
                      <span>{t.label}</span>
                      {t.count !== undefined && (
                        <span className="pill-tab__count">{t.count}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* About tab */}
              {activeTab === 'about' && (
                <div className="glass-panel up-about" style={{ padding: 20 }}>
                  <div className="up-about__grid">
                    {profile.gender && (
                      <>
                        <div className="up-about__label">gender</div>
                        <div className="up-about__value">{profile.gender.replace(/_/g, ' ')}</div>
                      </>
                    )}
                    {locationStr && (
                      <>
                        <div className="up-about__label">location</div>
                        <div className="up-about__value">
                          {locationStr}
                          {isVerified && (
                            <span className="up-verified"><CheckCircle size={11} /> verified</span>
                          )}
                        </div>
                      </>
                    )}
                    {profile.lookingFor && (
                      <>
                        <div className="up-about__label">looking for</div>
                        <div className="up-about__value">
                          <div className="up-about__tags">
                            <span className="tag-pill tag-pill--violet">{lookingForLabel(profile.lookingFor)}</span>
                          </div>
                        </div>
                      </>
                    )}
                    {interests.length > 0 && (
                      <>
                        <div className="up-about__label">interests</div>
                        <div className="up-about__value">
                          <div className="up-about__tags">
                            {interests.map((tag) => (
                              <span key={tag} className="tag-pill tag-pill--violet">{tag}</span>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    {lastActiveMonth && (
                      <>
                        <div className="up-about__label">last active</div>
                        <div className="up-about__value">{lastActiveMonth}</div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Posts tab */}
              {activeTab === 'posts' && (
                <>
                  {posts.length === 0 ? (
                    <div className="glass-panel up-empty" style={{ padding: 40, textAlign: 'center' }}>
                      <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>no posts yet.</div>
                    </div>
                  ) : (
                    <div className="profile-posts">
                      <div className="talk-list">
                        {posts.map((post) => (
                          <Link
                            key={post.id}
                            to={getMessageUrl(post)}
                            className="talk-item"
                            style={{ textDecoration: 'none' }}
                          >
                            <div className="content">
                              {post.title && <h3 className="title">{post.title}</h3>}
                              {post.body && (
                                <p className="body">
                                  {post.body.length > 200 ? post.body.slice(0, 200) + '...' : post.body}
                                </p>
                              )}
                              {post.categories && post.categories.length > 0 && (
                                <div className="tag-list">
                                  {post.categories.map((c) => (
                                    <span key={c} className="tag">{c}</span>
                                  ))}
                                </div>
                              )}
                              <div className="meta">
                                <LikeButton
                                  messageId={post.id}
                                  likes={post.likes ?? 0}
                                  likedBy={post.likedBy ?? []}
                                  onLikeChange={(newLikes: number, newLikedBy: string[]) => {
                                    setPosts((prev) =>
                                      prev.map((p) =>
                                        p.id === post.id
                                          ? { ...p, likes: newLikes, likedBy: newLikedBy }
                                          : p
                                      )
                                    );
                                  }}
                                />
                                <span className="muted">
                                  <MessageCircle size={14} /> {post.replies ?? 0}
                                </span>
                                <span className="dot">·</span>
                                <span className="muted">{formatTimestamp(post.timestamp)}</span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>

                      {hasMorePosts && (
                        <div className="load-more">
                          <button
                            className="btn btn-secondary"
                            onClick={loadMorePosts}
                            disabled={loadingMore}
                          >
                            {loadingMore ? 'loading...' : 'load more posts'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Badges tab */}
              {activeTab === 'badges' && (
                <div className="glass-panel up-badges" style={{ padding: 20 }}>
                  <div className="up-badges__header">
                    {earnedBadges.length} of {allBadgeDefs.length || '?'} earned
                  </div>
                  {allBadgeDefs.length > 0 ? (
                    <div className="up-badges__grid">
                      {allBadgeDefs.map((b) => (
                        <div key={b.id} className={`up-badge ${b.earned ? 'up-badge--earned' : ''}`}>
                          <div className={`up-badge__circle ${b.earned ? 'up-badge__circle--earned' : ''}`}>
                            <Award size={22} />
                          </div>
                          <div className="up-badge__label">{b.name || b.id}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <BadgeDisplay userId={userId!} variant="full" />
                  )}
                </div>
              )}
            </>
          )}

          {/* Profile not set fallback */}
          {profile && profile.profileSet === false && !loading && (
            <div className="up-error">
              <UserCheck size={32} />
              <h2>{handle || 'User'}</h2>
              <p>This user hasn't completed their profile yet.</p>
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                go back
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
